import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import Pagination from '../../common/Pagination'
import {
  defaultItemIcon,
} from '../../../icons'
import { getFormattedDate, getFromNowAsText, getFullDate } from '../../../helpers/timeHelper'
import {
  generateMamorioListCSV,
  getUserName,
  getLastStatus,
  getBatteryImageSrc,
  getLostFoundStatus,
  getMamorioImageSrc,
} from '../../../helpers/CommonMamorioHelper'
import {
  Filter, getTeamMamorios, updateGroupMamorio,
} from '../../../modules/Mamorio'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { getErrorString } from '../../../utils/errorUtils'
import { closeModal, showModal } from '../../../modules/UI'
import {
  BASE_MODAL,
  MAMORIO_DETAIL_MODAL,
  CONFIRM_MODAL,
  ERROR_MODAL,
} from '../../common/RootModal'
import EmptyListView from './EmptyMamorioList'
import { showLostAndBattery } from '../../../modules/common/session'
import HeaderType from '../../../modules/common/headerType'
import getSortIcon from '../../../modules/common/sortUtil'
import { returnRental } from '../../../modules/Rental'
import MamoriosMapDetailView from '../../overview/MamoriosMapDetailView'
import AutoReloadModal from '../../biz_plus/AutoReloadModal'

const AUTO_RELOAD_MIN_SECONDS = 10
const DEFAULT_AUTO_RELOAD_SECONDS = 30

type Props = {
  filter: Filter
  onChangeSort: (args: any) => any
  onClickPagination: (args: any) => any
  showMap?: boolean
  csvDownloadEnabled?: boolean
  rentalMode?: boolean
}

const MamorioList = ({
  filter,
  onChangeSort,
  onClickPagination,
  showMap,
  csvDownloadEnabled = false,
  rentalMode = false,
}: Props) => {
  const dispatch = useAppDispatch()
  const groups = useAppSelector((state) => state.team.groups)
  const mamorios = useAppSelector((state) => state.mamorio.mamorios)
  const memoEnabled = useMemo(
    () => Boolean(groups?.some((group) => group.memo_enabled)),
    [groups],
  )
  const autoReloadStorageKey = useMemo(() => (
    filter.teamDomain ? `mamorio_list_top_auto_reload_${filter.teamDomain}` : null
  ), [filter.teamDomain])
  const [autoReloadEnabled, setAutoReloadEnabled] = useState(false)
  const [autoReloadIntervalSeconds, setAutoReloadIntervalSeconds] = useState(DEFAULT_AUTO_RELOAD_SECONDS)
  const shouldShowAutoReloadControl = showMap && filter.teamDomain === 'grapestone_biz'

  const normalizeAutoReloadSeconds = useCallback((value: number) => (
    Math.max(AUTO_RELOAD_MIN_SECONDS, Math.floor(value))
  ), [])

  const onModalError = (message: string) => {
    if (message === null || message === undefined) return

    dispatch(closeModal({ modalType: ERROR_MODAL }))

    dispatch(showModal({
      modalType: ERROR_MODAL,
      modalProps: {
        message,
      },
    }))
  }

  const onClickForceReturnRental = async (targetMamorio: IMamorio, detailViewCallback: VoidFunction) => {
    if (!targetMamorio.group_id) return
    dispatch(showModal({
      modalType: CONFIRM_MODAL,
      modalProps: {
        message: '強制的に返却すると、持ち出しが解除され<br />所有者がオーナーに戻ります。よろしいですか？',
        confirmButtonText: '強制的に返却する',
        onActionConfirmed: async () => {
          try {
            await dispatch(returnRental({
              groupId: targetMamorio.group_id,
              mamorioId: targetMamorio.id,
              filter,
            })).unwrap()
            detailViewCallback()
          } catch (e: unknown) {
            onModalError(getErrorString(e))
          } finally {
            dispatch(closeModal({ modalType: CONFIRM_MODAL }))
          }
        },
      },
    }))
  }

  const onClickResetBattery = async (targetMamorio: IMamorio, detailViewCallback: VoidFunction) => {
    dispatch(showModal({
      modalType: CONFIRM_MODAL,
      modalProps: {
        message: '電池交換日を本日に設定します。よろしいですか？',
        confirmButtonText: 'はい',
        onActionConfirmed: async () => {
          try {
            await dispatch(updateGroupMamorio({
              mamorio: targetMamorio,
              params: { battery_life_resetted_at: getFullDate(new Date()) },
            })).unwrap()
            detailViewCallback()
            const fetchTeamMamorios = async (filterArg: Filter) => {
              try {
                await dispatch(getTeamMamorios({
                  filter: filterArg,
                })).unwrap()
              } catch (error: unknown) {
                // TODO: 例外処理
                console.error(getErrorString(error))
              }
            }
            fetchTeamMamorios(filter)
          } catch (e: unknown) {
            onModalError(getErrorString(e))
          } finally {
            dispatch(closeModal({ modalType: CONFIRM_MODAL }))
          }
        },
      },
    }))
  }

  const onClickList = (mamorio: IMamorio) => {
    dispatch(showModal({
      modalType: MAMORIO_DETAIL_MODAL,
      modalProps: {
        mamorio,
        onRentalForceReturnClick: (callback: VoidFunction) => onClickForceReturnRental(mamorio, callback),
        onClickResetBattery: (callback: VoidFunction) => onClickResetBattery(mamorio, callback),
      },
    }))
  }

  const fetchTeamMamoriosWithFilter = useCallback(async (filterArg: Filter) => {
    try {
      await dispatch(getTeamMamorios({
        filter: filterArg,
      })).unwrap()
    } catch (error: unknown) {
      // TODO: 例外処理
      console.error(getErrorString(error))
    }
  }, [dispatch])

  const showAutoReloadModal = useCallback(() => {
    dispatch(showModal({
      modalType: BASE_MODAL,
      modalProps: {
        title: '自動更新',
        onActionConfirmed: undefined,
        children: (
          <AutoReloadModal
            enabled={autoReloadEnabled}
            intervalSeconds={autoReloadIntervalSeconds}
            minSeconds={AUTO_RELOAD_MIN_SECONDS}
            onToggle={setAutoReloadEnabled}
            onIntervalChange={(value) => setAutoReloadIntervalSeconds(normalizeAutoReloadSeconds(value))}
          />
        ),
      },
    }))
  }, [autoReloadEnabled, autoReloadIntervalSeconds, dispatch, normalizeAutoReloadSeconds])

  useEffect(() => {
    fetchTeamMamoriosWithFilter(filter)
  }, [fetchTeamMamoriosWithFilter, filter])

  useEffect(() => {
    if (!autoReloadStorageKey) {
      setAutoReloadEnabled(false)
      setAutoReloadIntervalSeconds(DEFAULT_AUTO_RELOAD_SECONDS)
      return
    }

    const stored = localStorage.getItem(autoReloadStorageKey)
    if (!stored) {
      setAutoReloadEnabled(false)
      setAutoReloadIntervalSeconds(DEFAULT_AUTO_RELOAD_SECONDS)
      return
    }

    try {
      const parsed = JSON.parse(stored)
      if (typeof parsed === 'object' && parsed) {
        setAutoReloadEnabled(Boolean(parsed.enabled))
        setAutoReloadIntervalSeconds(
          typeof parsed.intervalSeconds === 'number'
            ? normalizeAutoReloadSeconds(parsed.intervalSeconds)
            : DEFAULT_AUTO_RELOAD_SECONDS,
        )
      } else {
        setAutoReloadEnabled(false)
        setAutoReloadIntervalSeconds(DEFAULT_AUTO_RELOAD_SECONDS)
      }
    } catch (error) {
      setAutoReloadEnabled(false)
      setAutoReloadIntervalSeconds(DEFAULT_AUTO_RELOAD_SECONDS)
    }
  }, [autoReloadStorageKey, normalizeAutoReloadSeconds])

  useEffect(() => {
    if (!autoReloadStorageKey) return
    localStorage.setItem(autoReloadStorageKey, JSON.stringify({
      enabled: autoReloadEnabled,
      intervalSeconds: autoReloadIntervalSeconds,
    }))
  }, [autoReloadEnabled, autoReloadIntervalSeconds, autoReloadStorageKey])

  useEffect(() => {
    if (!autoReloadEnabled || !shouldShowAutoReloadControl) return undefined

    const intervalId = window.setInterval(() => {
      fetchTeamMamoriosWithFilter(filter)
    }, autoReloadIntervalSeconds * 1000)

    return () => window.clearInterval(intervalId)
  }, [
    autoReloadEnabled,
    autoReloadIntervalSeconds,
    fetchTeamMamoriosWithFilter,
    filter,
    shouldShowAutoReloadControl,
  ])

  if (!mamorios || mamorios.length === 0) {
    return (<EmptyListView />)
  }

  const canDownloadCsv = csvDownloadEnabled && mamorios.length > 0
  const icon = getSortIcon(filter.sort, filter.order)
  const cursor = 'pointer'

  const downloadCsv = async () => {
    await generateMamorioListCSV(mamorios, {
      includeRentalStatus: rentalMode,
      fileName: `mamorio_list_${getFormattedDate(new Date(), 'YYYYMMDD_HHmmss')}.csv`,
    })
  }

  return (
    <>
      <div className="mamorio-settings">
        {
          showMap && (
            <div className="mamorios-map-list-top">
              <MamoriosMapDetailView
                mamorios={mamorios!}
                nameFilter={null}
                zoom={11}
              />
              {shouldShowAutoReloadControl && (
                <div className="mamorios-map-list-top__controls">
                  <button
                    type="button"
                    className="mamorios-map-list-top__auto-reload-button"
                    data-testid="mamorio-list-top-auto-reload-button"
                    onClick={showAutoReloadModal}
                  >
                    {autoReloadEnabled
                      ? `自動更新中...（${autoReloadIntervalSeconds}秒）`
                      : '自動更新'}
                  </button>
                </div>
              )}
            </div>
          )
        }
        <table className="table">
          <thead className="header" data-testid="mamorio-item-list-header">
            <tr>
              <th>
                <div
                  className="title"
                  style={{ cursor }}
                  onClick={() => onChangeSort(HeaderType.Name)}
                >
                  MAMORIOの名前
                  <img alt="sort" src={icon.name} />
                </div>
              </th>
              {memoEnabled && (
                <th>
                  <div
                    className="title"
                    style={{ cursor, minWidth: '40px' }}
                    onClick={() => onChangeSort(HeaderType.Memo)}
                  >
                    メモ
                    <img alt="sort" src={icon.memo} />
                  </div>
                </th>
              )}
              <th>
                <div
                  className="title"
                  style={{ cursor }}
                  onClick={() => onChangeSort(HeaderType.Time)}
                >
                  最終検知時刻
                  <img alt="sort" src={icon.time} />
                </div>
              </th>
              <th>
                <div className="title">
                  最後に検知した端末
                </div>
              </th>
              <th>
                <div
                  className="title"
                  style={{ cursor, minWidth: '75px' }}
                  onClick={() => onChangeSort(HeaderType.BatteryLifeRessetedAt)}
                >
                  バッテリー
                  <img alt="sort" src={icon.batteryLifeResettedAt} />
                </div>
              </th>
              {showLostAndBattery() && (
                <th>
                  <div
                    className="title"
                    style={{ cursor, minWidth: '40px' }}
                    onClick={() => onChangeSort(HeaderType.Status)}
                  >
                    状態
                    <img alt="sort" src={icon.status} />
                  </div>
                </th>
              )}
              <th>
                <div className="title">
                  所持者
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="list" data-testid="mamorio-item-list-body">
            {
              mamorios.map((mamorio: IMamorio) => (
                <tr
                  key={mamorio.id}
                  className="mamorio-item-list-item"
                  onClick={() => onClickList(mamorio)}
                >
                  <td className="item mamorio-item">
                    <div style={{ display: 'flex' }}>
                      <img width={40} className="image" src={getMamorioImageSrc(mamorio, defaultItemIcon)} alt="member" onError={(e) => { e.currentTarget.src = defaultItemIcon }} />
                      <div>
                        <span className="name">
                          {mamorio.name}
                        </span>
                        <br />
                        <span className="item-majorminor-text" style={{ fontSize: '12px', color: '#757575' }}>
                          {`${mamorio.major}-${mamorio.minor}`}
                        </span>
                      </div>
                    </div>
                  </td>
                  {memoEnabled && (
                    <td className="item mamorio-item">
                      <span className="name">
                        {mamorio.memo ? mamorio.memo : '-'}
                      </span>
                    </td>
                  )}
                  <td className="item item-last-detected-at">
                    {mamorio?.recent_log ? getFromNowAsText(mamorio.recent_log.at) : '-'}
                  </td>
                  <td className="item item-detected-by">
                    <img className="item-icon" src={getLastStatus(mamorio).imgSrc} style={{ borderRadius: 0 }} alt="status" />
                    {getLastStatus(mamorio).text}
                  </td>

                  {/* ----- バッテリー残量 ----- */}
                  <td className="item item-detected-by">
                    <div className="img-name-item">
                      <img className="item-icon" src={getBatteryImageSrc(mamorio)} style={{ borderRadius: 0 }} alt="status" />
                    </div>
                  </td>

                  {/* ----- 紛失ステータス ----- */}
                  {showLostAndBattery() && (
                    <td className="item item-detected-by">
                      <div className="img-name-item">
                        {getLostFoundStatus(mamorio)}
                      </div>
                    </td>
                  )}
                  <td className="item">
                    {mamorio.is_rent
                      ? (
                        <div className="sub">
                          <div className="status-rental rental">
                            持出中
                          </div>
                          <div className="rental-owner">
                            {mamorio.user ? getUserName(mamorio.user) : '所有者がいません'}
                          </div>
                        </div>
                      )
                      : (
                        <div className="sub">
                          <span className="rental-owner">
                            { mamorio.user ? getUserName(mamorio.user) : '-' }
                          </span>
                        </div>
                      )}
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>
        {canDownloadCsv && (
          <div className="mamorio-settings__index-footer">
            <div className="mamorio-settings__download-links">
              <span>ダウンロード:</span>
              <button
                type="button"
                className="mamorio-settings__download-link"
                onClick={downloadCsv}
              >
                CSV
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="pagination">
        <Pagination onChangePage={onClickPagination} />
      </div>
    </>
  )
}

MamorioList.defaultProps = {
  showMap: false,
  csvDownloadEnabled: false,
  rentalMode: false,
}

export default MamorioList
